import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, Alert, Badge, Button, Group, Tooltip } from "@mantine/core";
import type { MRT_ColumnDef } from "@ngt/mantine-react-table";
import { memo, useCallback, useMemo, type FC } from "react";
import { Link, Link as RouterLink, useParams } from "react-router-dom";
import { Permission, PersonnelRoleSummary } from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import DataGrid, {
    DefaultDataGridProps,
    mergeDataGridBodyRowProps,
    type DataGridBodyRowProps,
} from "src/components/common/dataGrid/DataGrid";
import DataGridDateCell from "src/components/common/dataGrid/cells/DataGridDateCell";
import DataGridPersonnelRoleIconCell from "src/components/common/dataGrid/cells/DataGridPersonnelRoleIconCell";
import DataGridStringCell from "src/components/common/dataGrid/cells/DataGridStringCell";
import type { IDataGridFilter } from "src/components/common/dataGrid/DataGridFilter";
import CollectionDataGridPage, {
    createInactiveDataGridBodyRowProps,
} from "src/components/layouts/pages/CollectionDataGridPage";
import type { CollectionDataGridPageGridProps } from "src/components/layouts/pages/CollectionDataGridPage";
import { LayoutPermissionMode } from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import useHasPermission from "src/hooks/authentication/useHasPermission";
import usePersonnelRoleSummariesByDepartmentId from "src/hooks/data/load/usePersonnelRoleSummariesByDepartmentId";
import useExpanded from "src/hooks/utilities/useExpanded";
import { parseRouteId } from "src/utilities/parseRouteId";

type PageParams = { institutionId?: string; departmentId?: string };

const inactiveRowProps =
    createInactiveDataGridBodyRowProps<PersonnelRoleSummary>("personnelRoleActive");

const filterFn = (summary: PersonnelRoleSummary, filter: IDataGridFilter) => {
    if (!filter.showInactive && !summary.personnelRoleActive) return false;
    if (!filter.search) return true;

    return !!(
        summary.personnelRoleRole?.toLocaleLowerCase().includes(filter.search) ||
        summary.personnelFullName?.toLocaleLowerCase().includes(filter.search) ||
        summary.personnelRoleEmail?.toLocaleLowerCase().includes(filter.search) ||
        summary.personnelRolePhone?.toLocaleLowerCase().includes(filter.search)
    );
};

const departmentPersonnelColumns: MRT_ColumnDef<PersonnelRoleSummary>[] = [
    {
        id: "personnelRoleId",
        header: " ",
        accessorKey: "personnelRoleId",
        Cell: DataGridPersonnelRoleIconCell as any,
        size: 50,
        grow: false,
        enableSorting: false,
    },
    {
        id: "personnelFullName",
        header: "Person",
        accessorKey: "personnelFullName",
        Cell: DataGridStringCell,
    },
    {
        id: "personnelRoleEmail",
        header: "Email",
        accessorKey: "personnelRoleEmail",
        Cell: DataGridStringCell,
    },
    {
        id: "personnelRolePhone",
        header: "Phone",
        accessorKey: "personnelRolePhone",
        Cell: DataGridStringCell,
    },
    {
        id: "personnelRoleDateStarted",
        header: "Date Started",
        accessorKey: "personnelRoleDateStarted",
        Cell: DataGridDateCell,
        size: 140,
        grow: false,
    },
    {
        id: "personnelRoleDateCeased",
        header: "Date Ceased",
        accessorKey: "personnelRoleDateCeased",
        Cell: DataGridDateCell,
        size: 140,
        grow: false,
    },
];

type PersonnelRoleGridProps = {
    data: PersonnelRoleSummary[];
    columns: MRT_ColumnDef<PersonnelRoleSummary>[];
    getRowHref?: (row: PersonnelRoleSummary) => string | undefined;
    mantineTableBodyRowProps?: DataGridBodyRowProps<PersonnelRoleSummary>;
};

const PersonnelRoleGrid: FC<PersonnelRoleGridProps> = memo(
    ({ data, columns, getRowHref, mantineTableBodyRowProps: configuredBodyRowProps }) => {
        const mantineTableBodyRowProps = useCallback(
            (rowProps) => {
                const { row } = rowProps;
                const href = getRowHref?.(row.original);

                return mergeDataGridBodyRowProps(rowProps, configuredBodyRowProps, {
                    ...(href ? { component: RouterLink, to: href } : {}),
                    style: {
                        textDecoration: "none",
                        cursor: href ? "pointer" : undefined,
                    },
                });
            },
            [configuredBodyRowProps, getRowHref],
        );

        if (data.length === 0) {
            return (
                <Alert color="information">There are currently no personnel for this role.</Alert>
            );
        }

        return (
            <DataGrid
                mantinePaperProps={{
                    ...DefaultDataGridProps.mantinePaperProps,
                    withBorder: false,
                    shadow: "none",
                }}
                data={data}
                columns={columns as any}
                mantineTableBodyRowProps={mantineTableBodyRowProps}
                initialState={DefaultDataGridProps.initialState}
                enablePagination={false}
                enableBottomToolbar={false}
            />
        );
    },
);

const DepartmentPersonnelAccordionGrid: FC<
    CollectionDataGridPageGridProps<PersonnelRoleSummary>
> = ({ data, columns, getRowHref, mantineTableBodyRowProps }) => {
    const { departmentId: departmentIdParam } = useParams<PageParams>();
    const departmentId = parseRouteId(departmentIdParam);

    const { roleGroups, accordionKeys } = useMemo(() => {
        const roleMap = new Map<string, { roleName: string; personnel: PersonnelRoleSummary[] }>();

        for (const personnelRole of data) {
            const roleName = personnelRole.personnelRoleRole ?? "Unknown Role";
            const roleKey = personnelRole.personnelRoleRoleId
                ? `role-${personnelRole.personnelRoleRoleId}`
                : `role-${roleName}`;
            const group = roleMap.get(roleKey) ?? { roleName, personnel: [] };

            group.personnel.push(personnelRole);
            roleMap.set(roleKey, group);
        }

        const roleGroups = [...roleMap.entries()]
            .map(([roleKey, group]) => ({ roleKey, ...group }))
            .sort((left, right) => left.roleName.localeCompare(right.roleName));

        return {
            roleGroups,
            accordionKeys: roleGroups.map(({ roleKey }) => roleKey),
        };
    }, [data]);

    const [expanded, setExpanded] = useExpanded(
        `institution-department-personnel-${departmentId}`,
        accordionKeys,
    );

    return (
        <Accordion
            variant="separated"
            multiple
            chevronPosition="left"
            value={expanded}
            onChange={setExpanded}
            chevron={<FontAwesomeIcon size="lg" icon={AppSettings.icons.arrowDown} />}
        >
            {roleGroups.map(({ roleKey, roleName, personnel }) => (
                <Accordion.Item key={roleKey} value={roleKey} style={{ overflow: "hidden" }}>
                    <Accordion.Control bg="primary" c="white">
                        <Group justify="space-between">
                            {roleName}
                            <Badge color="secondary.6" autoContrast>
                                {personnel.length}
                            </Badge>
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel styles={{ content: { padding: 0 } }}>
                        <PersonnelRoleGrid
                            data={personnel}
                            columns={columns}
                            getRowHref={getRowHref}
                            mantineTableBodyRowProps={mantineTableBodyRowProps}
                        />
                    </Accordion.Panel>
                </Accordion.Item>
            ))}
        </Accordion>
    );
};

const InstitutionDepartmentPersonnelPage: FC = () => {
    const { institutionId: institutionIdParam, departmentId: departmentIdParam } =
        useParams<PageParams>();
    const institutionId = parseRouteId(institutionIdParam);
    const departmentId = parseRouteId(departmentIdParam);
    const { data, loading, error } = usePersonnelRoleSummariesByDepartmentId(departmentId);

    const permissions = useCurrentUserPermissions();
    const canCreate = useHasPermission(
        permissions,
        Permission.InstitutionPersonnelCreate,
        undefined,
        institutionId,
    );

    const getRowHref = useCallback(
        (row: PersonnelRoleSummary) =>
            institutionId && departmentId && row.personnelRoleId
                ? `/institutions/${institutionId}/departments/${departmentId}/personnel/${row.personnelRoleId}`
                : undefined,
        [departmentId, institutionId],
    );

    return (
        <CollectionDataGridPage
            gridKey="institution-department-personnel"
            title="Department Personnel"
            data={data}
            loading={loading}
            error={error}
            columns={departmentPersonnelColumns}
            gridComponent={DepartmentPersonnelAccordionGrid}
            getRowHref={getRowHref}
            mantineTableBodyRowProps={inactiveRowProps}
            viewPermission={Permission.InstitutionPersonnelView}
            filterFn={filterFn}
            layoutContext={{ institutionId, departmentId }}
            layoutPermissionMode={LayoutPermissionMode.Any}
            actionButtons={
                <>
                    {canCreate && (
                        <Tooltip label="Add department personnel" withArrow>
                            <Button
                                component={Link}
                                to={`/institutions/${institutionId}/departments/${departmentId}/personnel/create`}
                            >
                                Add
                            </Button>
                        </Tooltip>
                    )}
                </>
            }
        />
    );
};

export default InstitutionDepartmentPersonnelPage;
